/**
 * Sales - wins, the pipeline behind them, and the work that feeds it.
 *
 * A win is a facility signed for one or more service lines, credited to one
 * rep, with its beds and (when known) a monthly value. Recording one makes
 * the account Active. A win is reversed with a reason, never deleted.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Trophy, Undo2 } from 'lucide-react';

import { Button } from '../../../components/new-components/ui/button';
import { Input } from '../../../components/new-components/ui/input';
import { Meta, Sale, ServiceLine, Summary, bdApi, fmtDate, fmtMoney, SERVICE_LABEL, STAGE_LABEL, STAGE_TONE, words } from './bdApi';
import { NativeSelect, Stat, StateLine } from './bdUi';
import { FacilityPicker } from './LeadsView';

export default function SalesView({ meta, onOpen, refreshKey, onChanged }: { meta: Meta; onOpen: (id: string) => void; refreshKey: number; onChanged: () => void }) {
  const [owner, setOwner] = useState('mine');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [sales, setSales] = useState<Sale[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    const [s, w] = await Promise.all([bdApi.summary(owner), bdApi.sales({ owner })]);
    if (s.success) setSummary(s.data); else setError(s.error || 'The summary could not be loaded.');
    if (w.success) setSales(w.data); else setError(w.error || 'Wins could not be loaded.');
  }, [owner]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const openStages = meta.stages.filter((s) => s.open);
  const maxOpen = summary ? Math.max(1, ...openStages.map((s) => summary.stages[s.key] || 0)) : 1;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <NativeSelect label="Whose numbers" value={owner} onChange={setOwner}>
          <option value="mine">Mine</option>
          <option value="all">The team</option>
          {meta.reps.filter((r) => r.id !== meta.me.id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </NativeSelect>
        <Button size="sm" onClick={() => setRecording(!recording)}><Trophy className="w-4 h-4 mr-1.5" /> Record a win</Button>
      </div>

      {recording && <RecordWin meta={meta} onDone={() => { setRecording(false); load(); onChanged(); }} onCancel={() => setRecording(false)} />}

      <StateLine error={error} onRetry={load} loading={!summary && !error} />

      {summary && (
        <>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
            <Stat label="Wins this month" value={summary.wins.mtd} sub={`${summary.wins.mtd_beds.toLocaleString()} beds · ${fmtMoney(summary.wins.mtd_value_cents)}/mo`} />
            <Stat label="Wins this quarter" value={summary.wins.qtd} sub={`${summary.wins.qtd_beds.toLocaleString()} beds · ${fmtMoney(summary.wins.qtd_value_cents)}/mo`} />
            <Stat label="Wins this year" value={summary.wins.ytd} />
            <Stat label="Open pipeline" value={summary.open_pipeline.toLocaleString()} sub="accounts still being sold" />
            <Stat label="Touches, 7 days" value={Object.values(summary.activity_7d).reduce((a, b) => a + (b || 0), 0)}
              sub={Object.entries(summary.activity_7d).map(([k, n]) => `${n} ${words(k).toLowerCase()}`).join(' · ') || 'none yet'} />
          </div>

          <section aria-labelledby="pipeline" className="rounded-lg border p-4">
            <h3 id="pipeline" className="text-sm font-semibold mb-3">Pipeline</h3>
            <ul className="space-y-1.5">
              {openStages.map((s) => {
                const n = summary.stages[s.key] || 0;
                return (
                  <li key={s.key} className="grid grid-cols-[8rem_1fr_3.5rem] items-center gap-3 text-sm">
                    <span>{STAGE_LABEL[s.key]}</span>
                    <span className="h-2.5 rounded-full bg-muted overflow-hidden" aria-hidden>
                      <span className="block h-full rounded-full" style={{ width: `${(n / maxOpen) * 100}%`, background: STAGE_TONE[s.key].dot }} />
                    </span>
                    <span className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{n.toLocaleString()}</span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              Active {summary.stages.ACTIVE.toLocaleString()} · Dropped {summary.stages.DROPPED.toLocaleString()} · Do not contact {summary.stages.DO_NOT_CONTACT.toLocaleString()}
              {summary.overdue_follow_ups > 0 && <> · <b className="text-destructive">{summary.overdue_follow_ups} overdue follow-ups</b></>}
            </p>
          </section>
        </>
      )}

      {sales && (
        <section aria-labelledby="wins">
          <h3 id="wins" className="text-sm font-semibold mb-2">Wins</h3>
          {sales.length === 0 ? <p className="text-sm text-muted-foreground">No wins recorded yet.</p> : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left text-muted-foreground">
                    <th className="py-2 px-3 font-medium">Signed</th>
                    <th className="py-2 px-3 font-medium">Facility</th>
                    <th className="py-2 px-3 font-medium">Service lines</th>
                    <th className="py-2 px-3 font-medium text-right">Beds</th>
                    <th className="py-2 px-3 font-medium text-right">Monthly</th>
                    <th className="py-2 px-3 font-medium">Rep</th>
                    <th className="py-2 px-3" />
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s) => <WinRow key={s.id} s={s} onOpen={onOpen} onReversed={() => { load(); onChanged(); }} />)}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function WinRow({ s, onOpen, onReversed }: { s: Sale; onOpen: (id: string) => void; onReversed: () => void }) {
  const reversed = s.status === 'REVERSED';
  const reverse = async () => {
    const reason = window.prompt('Why is this win being reversed?');
    if (!reason || reason.trim().length < 3) return;
    const r = await bdApi.reverseSale(s.id, reason.trim());
    if (r.success) onReversed(); else window.alert(r.error || 'Not reversed.');
  };
  return (
    <tr className={`border-t ${reversed ? 'text-muted-foreground' : ''}`}>
      <td className="py-2 px-3" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDate(s.signed_on)}</td>
      <td className="py-2 px-3">
        <button type="button" className={`hover:underline text-left ${reversed ? 'line-through' : 'font-medium'}`} onClick={() => onOpen(s.facility_id)}>{s.facility_name}</button>
        <div className="text-xs text-muted-foreground">{[s.facility_city, s.region].filter(Boolean).join(' · ')}</div>
        {reversed && <div className="text-xs">Reversed: {s.reversal_reason}</div>}
      </td>
      <td className="py-2 px-3">{s.service_lines.map((l) => SERVICE_LABEL[l]).join(', ')}</td>
      <td className="py-2 px-3 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.beds ?? '—'}</td>
      <td className="py-2 px-3 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(s.monthly_value_cents)}</td>
      <td className="py-2 px-3">{s.user_name || '—'}</td>
      <td className="py-2 px-3 text-right">
        {!reversed && <Button size="sm" variant="ghost" onClick={reverse} aria-label={`Reverse the win at ${s.facility_name}`}><Undo2 className="w-4 h-4" /></Button>}
      </td>
    </tr>
  );
}

function RecordWin({ meta, onDone, onCancel }: { meta: Meta; onDone: () => void; onCancel: () => void }) {
  const [facility, setFacility] = useState<{ id: string; name: string } | null>(null);
  const [lines, setLines] = useState<ServiceLine[]>([]);
  const [signedOn, setSignedOn] = useState(meta.today);
  const [beds, setBeds] = useState('');
  const [value, setValue] = useState('');
  const [rep, setRep] = useState(meta.me.id);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!facility || !lines.length) return;
    setBusy(true); setMsg(null);
    const r = await bdApi.recordSale({
      facility_id: facility.id, service_lines: lines, signed_on: signedOn,
      beds: beds ? Number(beds) : null, monthly_value: value ? Number(value) : null, note, user_id: rep,
    });
    setBusy(false);
    if (!r.success) { setMsg(r.error || 'Not recorded.'); return; }
    onDone();
  };

  return (
    <form onSubmit={submit} className="rounded-lg border p-4 space-y-3" aria-label="Record a win">
      <FacilityPicker value={facility} onChange={setFacility} />
      <fieldset>
        <legend className="text-xs text-muted-foreground mb-1">Service lines signed</legend>
        <div className="flex flex-wrap gap-2">
          {meta.service_lines.map((s) => {
            const on = lines.includes(s);
            return (
              <button key={s} type="button" aria-pressed={on} onClick={() => setLines(on ? lines.filter((x) => x !== s) : [...lines, s])}
                className={`rounded-full border px-3 py-1 text-xs ${on ? 'bg-foreground text-background border-foreground' : 'bg-background'}`}>{SERVICE_LABEL[s]}</button>
            );
          })}
        </div>
      </fieldset>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">Signed on<Input type="date" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} className="w-40" /></label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">Beds (blank = the facility's)<Input type="number" min={0} value={beds} onChange={(e) => setBeds(e.target.value)} className="w-40" /></label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">Monthly value ($, optional)<Input type="number" min={0} step="1" value={value} onChange={(e) => setValue(e.target.value)} className="w-44" /></label>
        <NativeSelect label="Credited to" value={rep} onChange={setRep}>
          {meta.reps.map((r) => <option key={r.id} value={r.id}>{r.name}{r.id === meta.me.id ? ' (me)' : ''}</option>)}
        </NativeSelect>
      </div>
      <Input aria-label="Note" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <p className="text-xs text-muted-foreground">Recording a win makes the account Active.</p>
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={busy || !facility || !lines.length}>{busy ? 'Recording…' : 'Record win'}</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        {msg && <span role="alert" className="text-sm text-destructive">{msg}</span>}
      </div>
    </form>
  );
}
